import { Fragment, PropsWithChildren } from "react";
import { Dialog, Transition } from "@headlessui/react";
import CloseIcon from "components/icons-svg/CloseIcon";
import { VariantProps, cva } from "utils/cva";
import { useUserAgentInfo } from "utils/hooks/useUserAgentInfo";
const drawerWrapper = cva("flex h-full", {
  variants: {
    intent: {
      top: ["justify-center items-start"],
      bottom: ["justify-center items-end"],
      left: ["justify-start"],
      right: ["justify-end"],
      bottomRight: ["justify-center items-end md:justify-end md:items-initial"]
    },
    size: {
      small: [],
      medium: [],
      mediumToFull: [],
      large: [],
      fullScreen: []
    }
  },
  defaultVariants: {
    intent: "bottom",
    size: "medium"
  }
});
const drawerBody = cva("flex relative flex-col bg-offWhite shadow-xl transition-all p-4", {
  variants: {
    intent: {
      top: ["rounded-none"],
      bottom: ["rounded-[20px_20px_0_0]"],
      left: ["rounded-none"],
      right: ["rounded-none"],
      bottomRight: ["rounded-[20px_20px_0_0] md:rounded-none"]
    },
    size: {
      small: ["md:h-[100svh]", "w-full", "min-w-[auto]", "md:max-w-[372px]"],
      medium: ["md:h-[100svh]", "w-full", "min-w-[auto]", "md:max-w-lg"],
      mediumToFull: ["h-[100svh]", "w-full", "min-w-[auto]", "max-w-[500px]"],
      large: ["md:h-[100svh]", "w-full", "min-w-[auto]", "md:max-w-2xl"],
      fullScreen: ["h-[100svh]", "w-full", "rounded-none"]
    }
  },
  defaultVariants: {
    intent: "bottom",
    size: "medium"
  }
});
interface DialogModelProps {
  isOpen: boolean;
  onClose?: () => void;
  className?: string;
  backDropClassName?: string;
  closeOnClickOutSide?: boolean;
  isCloseIcon?: boolean;
  classNameCloseIcon?: string;
  dialogZindex?: number;
  disableIOSInset?: boolean;
  classNameDialog?: string;
}
const transitionClasses = {
  top: {
    enterFrom: "-translate-y-full",
    enterTo: "translate-y-0",
    leaveFrom: "translate-y-0",
    leaveTo: "-translate-y-full"
  },
  bottom: {
    enterFrom: "translate-y-10",
    enterTo: "translate-y-0",
    leaveFrom: "translate-y-0",
    leaveTo: "translate-y-20"
  },
  left: {
    enterFrom: "-translate-x-full",
    enterTo: "translate-x-0",
    leaveFrom: "translate-x-0",
    leaveTo: "-translate-x-full"
  },
  right: {
    enterFrom: "translate-x-10 flex justify-end",
    enterTo: "translate-x-0 flex justify-end",
    leaveFrom: "translate-x-0 flex justify-end",
    leaveTo: "translate-x-20 flex justify-end"
  },
  bottomRight: {
    enterFrom: "translate-y-10 flex justify-end",
    enterTo: "translate-y-0 flex justify-end",
    leaveFrom: "translate-y-0 flex justify-end",
    leaveTo: "translate-y-20 flex justify-end"
  }
};
const CVADrawer = ({
  isOpen,
  onClose = () => {},
  children,
  intent,
  size,
  className = "",
  backDropClassName = "",
  closeOnClickOutSide = true,
  isCloseIcon = true,
  classNameCloseIcon = "",
  dialogZindex,
  disableIOSInset = false,
  classNameDialog
}: PropsWithChildren<DialogModelProps & VariantProps<typeof drawerBody>>) => {
  const {
    enterFrom,
    enterTo,
    leaveFrom,
    leaveTo
  } = transitionClasses[(intent as keyof typeof transitionClasses)] || {};
  const {
    isIOSAndSafari
  } = useUserAgentInfo();
  return <Transition show={isOpen} as={Fragment} data-sentry-element="Transition" data-sentry-component="CVADrawer" data-sentry-source-file="CVADrawer.tsx">
      <Dialog as='div' className={`relative ${dialogZindex ? `z-[${dialogZindex}]` : "z-[1999]"} ${classNameDialog}`} onClose={closeOnClickOutSide ? onClose : () => {}} data-sentry-element="Dialog" data-sentry-source-file="CVADrawer.tsx">
        <div className={`fixed inset-0 left-0 top-0 h-screen w-screen bg-[rgba(0,0,0,0.48)] ${backDropClassName}`} aria-hidden='true' />
        <div className='fixed inset-0 overflow-y-hidden'>
          <div className={drawerWrapper({
          intent,
          size
        })}>
            <Transition.Child className='w-full' enter='transition ease-out duration-500' enterFrom={`${enterFrom} w-full`} enterTo={`${enterTo} w-full`} leave='transition ease-in duration-150' leaveFrom={`${leaveFrom} w-full`} leaveTo={`${leaveTo} w-full`} data-sentry-element="Transition.Child" data-sentry-source-file="CVADrawer.tsx">
              <Dialog.Panel className={drawerBody({
              intent,
              size,
              className
            })} data-sentry-element="Dialog.Panel" data-sentry-source-file="CVADrawer.tsx">
                {isCloseIcon && <button aria-label='close drawer' className='btn btn-ghost absolute right-3 top-3 z-20 h-9 w-9 min-w-[0] rounded-lg px-1 py-1 text-secondary1000 hover:bg-gray-200/50 md:right-4 md:top-4' onClick={onClose}>
                    <CloseIcon className={`h-6 w-6 text-inherit ${classNameCloseIcon}`} />
                  </button>}
                {children}
                {isIOSAndSafari && !disableIOSInset && <div className='fixed left-0 top-[95vh] h-[100px] w-[100vw] bg-offWhite' />}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>;
};
export default CVADrawer;